import {
  type KeyboardEvent,
  type RefObject,
  type SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';

import { VisuallyHidden } from '../../core/components/visually-hidden/VisuallyHidden.js';
import { OverlayStackingContext } from '../../core/contexts/OverlayStacking.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';

/**
 * @public
 */
export interface OverlayTriggerState {
  /** Whether the overlay is currently open. */
  readonly isOpen: boolean;
  /** Opens the overlay. */
  open(): void;
  /** Closes the overlay. */
  close(): void;
  /** Toggles the overlay's visibility. */
  toggle(): void;
}

interface VisibleOverlayEntry {
  ref: RefObject<HTMLElement | null>;
  stackingLevel: number;
  close: () => void;
}

const visibleOverlays: VisibleOverlayEntry[] = [];

/**
 * Close all overlays with a stacking level equal to or higher than the
 * provided level, defaulting to 0 (close all other overlays).
 * @internal
 */
export function closeAllOverlays(level: number = 0): void {
  if (visibleOverlays.length === 0) {
    return;
  }

  // Close overlays from top to bottom
  for (let i = visibleOverlays.length - 1; i >= 0; i--) {
    const overlay = visibleOverlays[i];

    if (overlay.stackingLevel >= level) {
      overlay.close();
    } else {
      break;
    }
  }
}

/**
 * @public
 */
export type OverlayAria = {
  /** Props to apply to the overlay container element. */
  overlayProps: {
    onKeyDown?: (e: KeyboardEvent) => void;
    onFocus?: (e: FocusEvent) => void;
    onBlur?: (e: FocusEvent) => void;
  };
};

type FocusWithinResult = {
  focusWithinProps: {
    onFocus?: (e: FocusEvent) => void;
    onBlur?: (e: FocusEvent) => void;
  };
};

/** @internal */
export interface UseOverlayProps {
  /** Whether the overlay is currently open. */
  isOpen?: boolean;
  /** Handler that is called when the overlay should close. */
  onClose?: (reason: string) => void;
  /**
   * Whether to close the overlay when the user interacts outside it.
   * Known Issue: If set to true, clicking on some other overlays (e.g. Microguide)
   * also closes this overlay. The issue is irrelevant for smaller overlays that usually
   * don't contain a clickable overlay but might need some extra handling for bigger ones (e.g. Sheet).
   * @defaultValue false
   */
  isDismissible?: boolean;
  /** Whether the overlay should close when focus is lost or moves outside it. */
  shouldCloseOnBlur?: boolean;
  /**
   * Whether pressing the escape key to close the overlay should be disabled.
   * @defaultValue false
   */
  isKeyboardDismissDisabled?: boolean;
  /**
   * When user interacts with the argument element outside of the overlay ref,
   * return true if onClose should be called. This gives you a chance to filter
   * out interaction with elements that should not dismiss the overlay.
   * By default, onClose will always be called on interaction outside the overlay ref.
   */
  shouldCloseOnInteractOutside?: (element: HTMLElement) => boolean;
}

interface OverlayTriggerProps {
  /** Whether the overlay is open by default (controlled). */
  isOpen?: boolean;
  /** Whether the overlay is open by default (uncontrolled). */
  defaultOpen?: boolean;
  /** Handler that is called when the overlay's open state changes. */
  onOpenChange?: (isOpen: boolean) => void;
}

/**
 * Provides the behavior for overlays such as dialogs, popovers, and menus.
 * Hides the overlay when the user interacts outside it, when the Escape key is pressed,
 * or optionally, on blur. Only the top-most overlay will close at once.
 * @internal
 */
export function useOverlay(
  props: UseOverlayProps,
  ref: RefObject<HTMLElement | null>,
): OverlayAria {
  const {
    onClose,
    shouldCloseOnBlur,
    isOpen,
    isDismissible = false,
    isKeyboardDismissDisabled = false,
    shouldCloseOnInteractOutside,
  } = props;

  const overlayStacking = useContext(OverlayStackingContext);
  const hasPendingCloseRef = useRef(false);

  // Store onClose in a ref so that changes to the callback don't re-trigger
  // the visibleOverlays effect, which would disrupt the stack ordering.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Reset the close lock once the overlay is actually closed.
  useEffect(() => {
    if (!isOpen) {
      hasPendingCloseRef.current = false;
    }
  }, [isOpen]);

  const requestClose = useCallback(
    (reason: string, onlyIfTopMost = true) => {
      if (!onCloseRef.current || !isOpen || hasPendingCloseRef.current) {
        return;
      }

      if (onlyIfTopMost) {
        const topEntry = visibleOverlays[visibleOverlays.length - 1];
        if (topEntry?.ref !== ref) {
          return;
        }
      }

      hasPendingCloseRef.current = true;
      onCloseRef.current(reason);
    },
    [isOpen, ref],
  );

  // Add the overlay ref to the stack of visible overlays on mount, and remove on unmount.
  useEffect(() => {
    if (isOpen && onCloseRef.current) {
      const entry: VisibleOverlayEntry = {
        ref,
        stackingLevel: overlayStacking,
        close: () => requestClose('programmatic', false),
      };
      visibleOverlays.push(entry);

      return () => {
        const index = visibleOverlays.findIndex((e) => e.ref === ref);
        if (index >= 0) {
          visibleOverlays.splice(index, 1);
        }
      };
    }

    return () => {
      const index = visibleOverlays.findIndex((e) => e.ref === ref);
      if (index >= 0) {
        visibleOverlays.splice(index, 1);
      }
    };
  }, [isOpen, ref, overlayStacking, requestClose]);

  const onInteractOutside = (e: SyntheticEvent<Element>) => {
    if (
      !shouldCloseOnInteractOutside ||
      shouldCloseOnInteractOutside(e.target as HTMLElement)
    ) {
      requestClose('backdrop');
    }
  };

  const onInteractOutsideStart = (e: SyntheticEvent<Element>) => {
    if (!isDismissible || !shouldCloseOnBlur) {
      return;
    }

    if (
      !shouldCloseOnInteractOutside ||
      shouldCloseOnInteractOutside(e.target as HTMLElement)
    ) {
      requestClose('blur');
    }
  };

  // Handle the escape key
  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isKeyboardDismissDisabled) {
        event.preventDefault();
        requestClose('escape');
      }
      // captures all keyboard events within the overlay to not affect underlaying elements.
      event.stopPropagation();
    },
    [isKeyboardDismissDisabled, requestClose],
  );

  // Handle clicking outside the overlay to close it
  useInteractOutside({
    ref,
    onInteractOutside: isDismissible ? onInteractOutside : undefined,
    onInteractOutsideStart,
  });

  const { focusWithinProps } = useFocusWithin({
    isDisabled: !shouldCloseOnBlur,
    onBlurWithin: (e) => {
      if (
        !shouldCloseOnInteractOutside ||
        shouldCloseOnInteractOutside(e.relatedTarget as HTMLElement)
      ) {
        requestClose('blur');
      }
    },
  });

  return {
    overlayProps: {
      onKeyDown,
      ...focusWithinProps,
    },
  };
}

/**
 * Manages state for an overlay trigger. Tracks whether the overlay is open, and provides
 * methods to toggle this state.
 * @internal
 */
export function useOverlayTriggerState(
  props: OverlayTriggerProps,
): OverlayTriggerState {
  const onOpenChange =
    props.onOpenChange ??
    ((isOpen: boolean) => {
      /* empty */
    });
  const [isOpenValue, setOpen] = useControllableState({
    defaultValue: props.defaultOpen || false,
    value: props.isOpen,
    onChange: onOpenChange,
  });

  const isOpen = isOpenValue ?? false;
  return {
    isOpen,
    open: useCallback(() => setOpen(true), [setOpen]),
    close: useCallback(() => setOpen(false), [setOpen]),
    toggle: useCallback(() => setOpen(!isOpen), [isOpen, setOpen]),
  };
}

interface InteractOutsideProps {
  ref: RefObject<Element | null>;
  onInteractOutside?: (e: SyntheticEvent) => void;
  onInteractOutsideStart?: (e: SyntheticEvent) => void;
  /** Whether the interact outside events should be disabled. */
  isDisabled?: boolean;
}

/**
 * Example, used in components like Dialogs and Popovers so they can close
 * when a user clicks outside them.
 */
function useInteractOutside(props: InteractOutsideProps) {
  const { ref, onInteractOutside, isDisabled, onInteractOutsideStart } = props;
  const stateRef = useRef({
    isPointerDown: false,
    ignoreEmulatedMouseEvents: false,
    onInteractOutside,
    onInteractOutsideStart,
  });
  const state = stateRef.current;
  state.onInteractOutside = onInteractOutside;
  state.onInteractOutsideStart = onInteractOutsideStart;

  useEffect(() => {
    if (isDisabled) {
      return;
    }

    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (isValidEvent(e, ref) && state.onInteractOutside) {
        if (state.onInteractOutsideStart) {
          // This is a direct copy from the react-aria useInteractOutside
          // but they do not have strict mode enabled, which allows them
          // to simply pass any event to the onInteractOutside,
          // which expects a SyntheticEvent.
          state.onInteractOutsideStart(e as unknown as SyntheticEvent);
        }
        state.isPointerDown = true;
      }
    };

    // Use pointer events if available. Otherwise, fall back to mouse and touch events.
    if (typeof PointerEvent !== 'undefined') {
      const onPointerUp = (e: MouseEvent) => {
        if (
          state.isPointerDown &&
          state.onInteractOutside &&
          isValidEvent(e, ref)
        ) {
          state.isPointerDown = false;
          // This is a direct copy from the react-aria useInteractOutside
          // but they do not have strict mode enabled, which allows them
          // to simply pass any event to the onInteractOutside,
          // which expects a SyntheticEvent.
          state.onInteractOutside(e as unknown as SyntheticEvent);
        }
      };

      document.body.addEventListener('pointerdown', onPointerDown);
      document.body.addEventListener('pointerup', onPointerUp);

      return () => {
        document.body.removeEventListener('pointerdown', onPointerDown);
        document.body.removeEventListener('pointerup', onPointerUp);
      };
    } else {
      const onMouseUp = (e: MouseEvent) => {
        if (state.ignoreEmulatedMouseEvents) {
          state.ignoreEmulatedMouseEvents = false;
        } else if (
          state.isPointerDown &&
          state.onInteractOutside &&
          isValidEvent(e, ref)
        ) {
          state.isPointerDown = false;
          // This is a direct copy from the react-aria useInteractOutside
          // but they do not have strict mode enabled, which allows them
          // to simply pass any event to the onInteractOutside,
          // which expects a SyntheticEvent.
          state.onInteractOutside(e as unknown as SyntheticEvent);
        }
      };

      const onTouchEnd = (e: TouchEvent) => {
        state.ignoreEmulatedMouseEvents = true;
        if (
          state.onInteractOutside &&
          state.isPointerDown &&
          isValidEvent(e, ref)
        ) {
          state.isPointerDown = false;
          // This is a direct copy from the react-aria useInteractOutside
          // but they do not have strict mode enabled, which allows them
          // to simply pass any event to the onInteractOutside,
          // which expects a SyntheticEvent.
          state.onInteractOutside(e as unknown as SyntheticEvent);
        }
      };

      document.body.addEventListener('mousedown', onPointerDown);
      document.body.addEventListener('mouseup', onMouseUp);
      document.body.addEventListener('touchstart', onPointerDown);
      document.body.addEventListener('touchend', onTouchEnd);

      return () => {
        document.body.removeEventListener('mousedown', onPointerDown);
        document.body.removeEventListener('mouseup', onMouseUp);
        document.body.removeEventListener('touchstart', onPointerDown);
        document.body.removeEventListener('touchend', onTouchEnd);
      };
    }
  }, [ref, state, isDisabled]);
}

/** Is the pointer event valid */
function isValidEvent(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  event: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ref: any,
) {
  if (event.button > 0) {
    return false;
  }

  // if the event target is no longer in the document
  if (event.target) {
    const ownerDocument = event.target.ownerDocument;
    if (
      !ownerDocument ||
      !ownerDocument.documentElement.contains(event.target)
    ) {
      return false;
    }
  }

  return !!ref.current && !ref.current.contains(event.target);
}

interface FocusWithinProps {
  /** Whether the focus within events should be disabled. */
  isDisabled?: boolean;
  /** Handler that is called when the target element or a descendant receives focus. */
  onFocusWithin?: (e: FocusEvent) => void;
  /** Handler that is called when the target element and all descendants lose focus. */
  onBlurWithin?: (e: FocusEvent) => void;
  /** Handler that is called when the the focus within state changes. */
  onFocusWithinChange?: (isFocusWithin: boolean) => void;
}

/**
 * Handles focus events for the target and its descendants.
 */
function useFocusWithin(props: FocusWithinProps): FocusWithinResult {
  const state = useRef({
    isFocusWithin: false,
  }).current;

  if (props.isDisabled) {
    return { focusWithinProps: {} };
  }

  const onFocus = (e: FocusEvent) => {
    if (!state.isFocusWithin) {
      if (props.onFocusWithin) {
        props.onFocusWithin(e);
      }

      if (props.onFocusWithinChange) {
        props.onFocusWithinChange(true);
      }

      state.isFocusWithin = true;
    }
  };

  const onBlur = (e: FocusEvent) => {
    // We don't want to trigger onBlurWithin and then immediately onFocusWithin again
    // when moving focus inside the element. Only trigger if the currentTarget doesn't
    // include the relatedTarget (where focus is moving).
    const currentTarget = e.currentTarget as HTMLElement;
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (state.isFocusWithin && !currentTarget.contains(relatedTarget)) {
      if (props.onBlurWithin) {
        props.onBlurWithin(e);
      }

      if (props.onFocusWithinChange) {
        props.onFocusWithinChange(false);
      }

      state.isFocusWithin = false;
    }
  };

  return {
    focusWithinProps: {
      onFocus: onFocus,
      onBlur: onBlur,
    },
  };
}

/**
 * @public
 */
export interface DismissButtonProps {
  /** Called when the dismiss button is activated. */
  onDismiss?: () => void;
}

/**
 * A visually hidden button that can be used to allow screen reader
 * users to dismiss a modal or popup when there is no visual
 * affordance to do so.

 * @public
 */
export function DismissButton(props: Readonly<DismissButtonProps>) {
  const { onDismiss } = props;
  const onClick = () => {
    if (onDismiss) {
      onDismiss();
    }
  };

  return (
    <VisuallyHidden>
      <button tabIndex={-1} aria-label="dismiss" onClick={onClick} />
    </VisuallyHidden>
  );
}
